/**
 * Frame24 – Death certificate workflow completion (IAD)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ListChecks } from 'lucide-react';
import { Button } from '../ui/Button';
import { SettlementLayout } from '../settlement/SettlementLayout';
import { apiService } from '../../utils/api';
import {
  DEATH_CERT_FRAME_DATA,
  DEATH_CERT_STEP_ID,
  DEATH_CERT_API_PHASE,
  deathCertRoute,
} from '../../utils/deathCertWorkflow';
import { listSettlementDocuments } from '../../utils/settlementDocumentsClient';

type SnapKey = keyof typeof DEATH_CERT_FRAME_DATA;

export default function Frame24() {
  const navigate = useNavigate();
  const { estateId } = useParams<{ estateId?: string }>();

  const [snap, setSnap] = useState<Partial<Record<SnapKey, Record<string, unknown>>>>({});
  const [docCount, setDocCount] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);
  const [completionNote, setCompletionNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!estateId) {
      setSnap({});
      setDocCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadError(null);
      try {
        const entries = await Promise.all(
          (Object.keys(DEATH_CERT_FRAME_DATA) as SnapKey[]).map(async (key) => {
            const frameId = DEATH_CERT_FRAME_DATA[key];
            const res = (await apiService.request(`/api/settlement/${estateId}/frame/${frameId}`, {
              method: 'GET',
            })) as { success?: boolean; data?: { data?: Record<string, unknown> | null } | null };
            const row = res.data;
            const blob =
              row && typeof row === 'object' && 'data' in row && row.data && typeof row.data === 'object'
                ? (row.data as Record<string, unknown>)
                : {};
            return [key, blob] as const;
          })
        );
        if (cancelled) return;
        setSnap(Object.fromEntries(entries) as Partial<Record<SnapKey, Record<string, unknown>>>);
        const docs = await listSettlementDocuments(estateId, DEATH_CERT_API_PHASE, DEATH_CERT_STEP_ID);
        if (!cancelled) setDocCount(docs.length);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load summary');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId]);

  const markStepComplete = async () => {
    if (!estateId) return;
    setSaving(true);
    try {
      await apiService.request(`/api/settlement/${estateId}/progress/${DEATH_CERT_API_PHASE}/${DEATH_CERT_STEP_ID}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: 'complete',
          notes: completionNote || 'Marked complete from death certificate workflow (Frames 20–24).',
        }),
      });
      navigate(estateId ? `/sframe-141/${estateId}` : '/sframe-141');
    } catch (e) {
      console.error(e);
      alert('Could not update settlement progress. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const order = snap.orderForm || {};
  const orders = Array.isArray(snap.orders?.orders) ? (snap.orders?.orders as unknown[]) : [];
  const received = snap.received || {};
  const institutions = Array.isArray(snap.distribution?.institutions)
    ? (snap.distribution?.institutions as unknown[])
    : [];

  return (
    <SettlementLayout currentPhase="iad" currentStep="death-certificates" estateId={estateId ?? null}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-start gap-3">
          <ListChecks className="w-8 h-8 text-cyan-700 shrink-0 mt-1" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Certificate Tracking Complete</h1>
            <p className="text-gray-600">
              Review what you captured, then mark the &quot;Obtain Death Certificates&quot; settlement step complete when
              you are satisfied.
            </p>
          </div>
        </div>

        {loadError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            {loadError}
          </div>
        )}

        <div className="space-y-4 mb-8">
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Order (Frame 244)</h2>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>Channel: {String(order.orderSource || '—')}</li>
              <li>Provider: {String(order.orderedThrough || '—')}</li>
              <li>Copies ordered: {order.copiesOrdered != null && order.copiesOrdered !== '' ? String(order.copiesOrdered) : '—'}</li>
              <li>Date ordered: {String(order.dateOrdered || '—')}</li>
            </ul>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Orders tracked (Frame 21)</h2>
            <p className="text-sm text-gray-700">{orders.length} line(s) logged.</p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Received (Frame 22)</h2>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>Copies received: {received.copiesReceived != null && received.copiesReceived !== '' ? String(received.copiesReceived) : '—'}</li>
              <li>Date received: {String(received.dateReceived || '—')}</li>
            </ul>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Distribution (Frame 23)</h2>
            <p className="text-sm text-gray-700">{institutions.length} institution row(s).</p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Vault documents</h2>
            <p className="text-sm text-gray-700">{docCount} file(s) on record for this step.</p>
          </div>
        </div>

        <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-5 mb-6">
          <label className="flex items-start gap-3 text-sm text-cyan-950">
            <input
              type="checkbox"
              className="mt-1"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span>
              I confirm I have ordered, received, and distributed death certificates according to my institution list,
              or I have noted follow-up items elsewhere in settlement notes.
            </span>
          </label>
        </div>

        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-800 mb-1">Optional note for step progress</label>
          <textarea
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[72px]"
            value={completionNote}
            onChange={(e) => setCompletionNote(e.target.value)}
            placeholder="Anything co-executors should know about remaining certificate needs."
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
          <Button variant="outline" onClick={() => navigate(deathCertRoute('/frame-23', estateId))} disabled={saving}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => navigate(deathCertRoute('/frame-20', estateId))} disabled={saving}>
              Back to overview
            </Button>
            <Button
              onClick={() => void markStepComplete()}
              size="lg"
              disabled={!estateId || !acknowledged || saving}
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Mark step complete
            </Button>
          </div>
        </div>

        {!estateId && (
          <p className="text-sm text-amber-800 mt-4">
            Open this page with an estate ID in the URL to mark the settlement step complete on the server.
          </p>
        )}
      </div>
    </SettlementLayout>
  );
}
